import React, { useCallback, useEffect, useState } from "react";
import {
  API,
  defaultLimitPagination,
  deleteCall,
  get,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../config";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  formatMobile,
  formatName,
  formatPhone,
  formatSubcontractorName,
  getModuleNamingBySlug,
} from "../../lib/Misc";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../../components/Pagination";
import { debounce } from "lodash";
import VendorActionButton from "./VendorActionButton";
import MainBody from "../../components/MainBody/MainBody";
import { ResponsiveDrawerForm } from "../../components/ResponsiveDrawerForm";
import AddEditVendor from "./AddEditVendor";
import { LucidePlusCircle } from "lucide-react";
import sortCaret from "../../components/SortCaret";
// import { useTrust } from "../../lib/context/TrustContext";
import { useOrganization } from "../../lib/context/OrganisationContext";
import UploadVendor from "./UploadVendor";
import Confirmation from "../../components/Confirmation";
import CommonFilters from "../../components/CommonFilters";

// * Start: Vendor Listing Page
const Vendors = ({ org = false }) => {
  // * Start: Url Management State

  const { organizationData } = useOrganization();

  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");

  // * End: Url Management State

  // * Start: List Management State

  // const { trustId } = useTrust();
  const params = useParams();
  const trustId = params?.trust_id;
  const [vendorsList, setVendorsList] = useState([]);
  const [vendorLoading, setVendorLoading] = useState(false);
  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [searchParam, setSearchParam] = useState(search || "");
  const [searchParamData, setSearchParamData] = useState(search || "");
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();

  // * End: List Management State

  // * Start: Single Vendor Action State

  const [edit, setEdit] = useState(false);
  const [vendorId, setVendorId] = useState(null);
  const [showAddEditVendor, setShowAddEditVendor] = React.useState(false);
  const [showVendorDelete, setShowVendorDelete] = useState(false);

  const [amlCtfContactId, setAmlCtfContactId] = useState(org ? "1" : "");

  const [trustIds, setTrustIds] = useState();

  useEffect(() => {
    if (state?.vendorId) {
      setEdit(true);
      setVendorId(state?.vendorId);
      toggleShowAddEditVendor();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.vendorId]);

  // * End: Single Vendor Action State

  const toggleShowAddEditVendor = () => {
    if (showAddEditVendor) {
      setEdit(false);
      setVendorId(null);
    }
    setShowAddEditVendor(!showAddEditVendor);
  };

  const toggleShowDelete = () => {
    if (showVendorDelete) {
      setVendorId(null);
    }
    setShowVendorDelete(!showVendorDelete);
  };

  /**
   * * Start: Fetch Vendor List Form The API
   * @param {Number} passLimit (Number of Limit for each page)
   * @param {Number} passPage (Number of Page for records)
   * @returns {Array} Vendor List
   **/

  const fetchVendors = useCallback(
    async (passLimit, passPage) => {
      try {
        setVendorLoading(true);
        const { data } = await get(
          org
            ? `${API.GET_VENDORS}?limit=${
                passLimit ? passLimit : limit ? limit : defaultLimitPagination
              }&page=${passPage ? passPage : page ? page : 1}&search=${
                searchParam ? searchParam : ""
              }&sort_column=${sortField ? sortField : ""}&sort_order=${
                sortOrder ? sortOrder : ""
              }&trust_ids=${trustIds ? trustIds : ""}&organization_id=${
                organizationData?.organizationId
              }&is_aml_ctf=${amlCtfContactId ? amlCtfContactId : ""}`
            : `${API.GET_VENDORS}?limit=${
                passLimit ? passLimit : limit ? limit : defaultLimitPagination
              }&page=${passPage ? passPage : page ? page : 1}&search=${
                searchParam ? searchParam : ""
              }&sort_column=${sortField ? sortField : ""}&sort_order=${
                sortOrder ? sortOrder : ""
              }&trust_id=${trustId ? trustId : ""}`
        );
        setVendorLoading(false);
        setLimit(data?.data?.per_page);
        setPage(data?.data?.current_page);
        setTotal(data?.data?.total);
        setFrom(data?.data?.from);
        setTo(data?.data?.to);
        const vendorsList = data.data.data;
        setVendorsList(vendorsList);
        return data.data;
      } catch (error) {
        setVendorsList([]);
        setVendorLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      searchParam,
      sortOrder,
      sortField,
      trustId,
      trustIds,
      organizationData?.organizationId,
      amlCtfContactId,
    ]
  );

  //* End: Fetch Vendor List Form The API

  /**
   * * Start: Vendor Table List Record Generation
   * @param {Number} quantity (Number of Vendors)
   * @returns {Array} Vendor List
   **/

  const vendorsGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: vendorsList[i]?.id,
        vendor_id: vendorsList[i]?.id ? vendorsList[i]?.id : "",
        trust: vendorsList[i]?.trust
          ? formatSubcontractorName(vendorsList[i]?.trust)
          : "AML-CTF",
        code: vendorsList[i]?.code ? vendorsList[i]?.code : "",
        name:
          formatName(vendorsList[i]?.first_name, vendorsList[i]?.last_name) +
          (vendorsList[i]?.business_name
            ? ` (${vendorsList[i]?.business_name})`
            : ""),
        joint_name: vendorsList[i]?.joint_accounts?.length
          ? formatSubcontractorName(vendorsList[i])
          : "-",
        email: vendorsList[i]?.email,
        phone: vendorsList[i]?.phone ? formatPhone(vendorsList[i]?.phone) : "-",
        mobile: vendorsList[i]?.mobile
          ? formatMobile(vendorsList[i]?.mobile)
          : "-",
        action: (
          <VendorActionButton
            id={vendorsList[i]?.id}
            org={org}
            used={vendorsList[i]?.is_used ? true : false}
            toggleShowDelete={toggleShowDelete}
            setEdit={setEdit}
            setVendorId={setVendorId}
            toggleShowAddEditVendor={toggleShowAddEditVendor}
            trustId={trustId}
            vendor={vendorsList[i]}
          />
        ),
      });
    }
    return items;
  };

  const vendors = vendorsGenerator(vendorsList?.length);

  //* End: Vendor Table List Record Generation

  //* Start: Vendor List Columns

  const columns = org
    ? [
        {
          dataField: "trust",
          text: "Trust",
          sort: true,
          sortCaret,
          headerClasses: THClasses,
          classes: TDClasses,
        },
        {
          dataField: "code",
          text: "Code",
          sort: true,
          sortCaret,
          headerClasses: THClasses,
          classes: TDClasses,
        },
        {
          dataField: "name",
          text: "Name",
          sort: true,
          sortCaret,
          headerClasses: THClasses,
          classes: TDClasses,
        },
        {
          headerClasses: THClasses,
          classes: TDClasses,
          dataField: "joint_name",
          text: "Joint Name",
          sort: true,
          sortCaret,
        },
        {
          dataField: "email",
          text: "Email",
          sort: true,
          sortCaret,
          headerClasses: THClasses,
          classes: TDClasses,
        },
        {
          dataField: "phone",
          text: "Phone No.",
          sort: true,
          sortCaret,
          headerClasses: THClasses,
          classes: TDClasses,
        },
        {
          dataField: "mobile",
          text: "Mobile No.",
          sort: true,
          sortCaret,
          headerClasses: THClasses,
          classes: TDClasses,
        },
        {
          dataField: "action",
          text: "Action",
          align: "center",
          headerAlign: "center",
          // style: { width: "127px" },
          headerClasses: THClasses,
          classes: TDClasses,
        },
      ]
    : [
        {
          dataField: "code",
          text: "Code",
          sort: true,
          sortCaret,
          headerClasses: THClasses,
          classes: TDClasses,
        },
        {
          dataField: "name",
          text: "Name",
          sort: true,
          sortCaret,
          headerClasses: THClasses,
          classes: TDClasses,
        },
        {
          headerClasses: THClasses,
          classes: TDClasses,
          dataField: "joint_name",
          text: "Joint Name",
          sort: true,
          sortCaret,
        },
        {
          dataField: "email",
          text: "Email",
          sort: true,
          sortCaret,
          headerClasses: THClasses,
          classes: TDClasses,
        },
        {
          dataField: "phone",
          text: "Phone No.",
          sort: true,
          sortCaret,
          headerClasses: THClasses,
          classes: TDClasses,
        },
        {
          dataField: "mobile",
          text: "Mobile No.",
          sort: true,
          sortCaret,
          headerClasses: THClasses,
          classes: TDClasses,
        },
        {
          dataField: "action",
          text: "Action",
          align: "center",
          headerAlign: "center",
          // style: { width: "127px" },
          headerClasses: THClasses,
          classes: TDClasses,
        },
      ];

  //* End: Vendor List Columns

  /**
   * * Start: Vendor List Sorting
   * @param {type} (Sort)
   * @param {JSON} (page, sortField, sortOrder) (To get the current value of the table)
   **/
  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  //* End: Vendor List Sorting

  /**
   * * Start: Vendor List Change Limit
   * @param {Event} e (Limit Number)
   **/
  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchVendors(limit, 1);
  };

  //* End: Vendor List Change Limit

  /**
   * * Start: Vendor List Change Page
   * @param {Number} page (Page Number)
   **/
  const handleChangePage = (page) => {
    setPage(page);
    fetchVendors(limit, page);
  };

  //* End: Vendor List Change Page

  //* Start: Vendor List and URL Mangement useEffect

  useEffect(() => {
    navigate(
      `?limit=${limit ? limit : defaultLimitPagination}&page=${
        page ? page : 1
      }&search=${searchParam ? searchParam : ""}&sort_column=${
        sortField ? sortField : ""
      }&sort_order=${sortOrder ? sortOrder : ""}`,
      {
        replace: true,
      }
    );
  }, [navigate, limit, searchParam, page, sortField, sortOrder]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  //* End: Vendor List and URL Mangement useEffect

  //* Start: Vendor List Search Record Management

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    []
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    if (nextValue?.length >= 2 || nextValue?.length === 0) {
      debouncedSave(nextValue);
    }
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  //* End: Vendor List Search Record Management

  // Single Vendor Delete
  const handleVendorDelete = async () => {
    try {
      setVendorLoading(true);
      await deleteCall(`${API.VENDOR_DELETE}/${vendorId}`);
      setVendorLoading(false);
      fetchVendors(limit, page);
    } catch (error) {
      setVendorLoading(false);
    }
  };

  return (
    <>
      <MainBody
        searchParamData={searchParamData}
        handleClearSearch={handleClearSearch}
        handleSearchChange={handleSearchChange}
        mainTitle={getModuleNamingBySlug(
          organizationData?.organizationData,
          "vendors",
          "Vendors"
        )}
        actionContent={
          <>
            {org && (
              <CommonFilters
                trust={org}
                trustId={trustIds}
                multiTrust={org}
                setTrustId={setTrustIds}
                aml_ctf_contact={true}
                amlCtfContactId={amlCtfContactId}
                setAmlCtfContactId={setAmlCtfContactId}
              />
            )}
            {/* Import Vendor */}
            {!org && (
              <UploadVendor
                fetchData={() => fetchVendors(limit, page)}
                trustId={trustId}
                ButtonName={"Import"}
                setLoading={setVendorLoading}
                organizationData={organizationData}
              />
            )}

            {/* Add/Edit Vendor Modal */}
            <ResponsiveDrawerForm
              description={""}
              title={
                edit
                  ? `Edit ${getModuleNamingBySlug(
                      organizationData?.organizationData,
                      "vendors",
                      "Vendor"
                    )}`
                  : `Add ${getModuleNamingBySlug(
                      organizationData?.organizationData,
                      "vendors",
                      "Vendor"
                    )}`
              }
              handleOpen={() => {}}
              handleClose={() => {
                setEdit(false);
                setVendorId(null);
                setShowAddEditVendor(false);
              }}
              buttonText={
                <div className="flex items-center gap-1">
                  <LucidePlusCircle size={18} />
                  <span>
                    Add{" "}
                    {getModuleNamingBySlug(
                      organizationData?.organizationData,
                      "vendors",
                      "Vendor"
                    )}
                  </span>
                </div>
              }
              className={"overflow-hidden"}
              open={showAddEditVendor}
              buttonClassName={""}
              setOpen={setShowAddEditVendor}
              mobileCancelHidden={true}
              write={true}
            >
              <AddEditVendor
                edit={edit}
                toggleShowAddEditVendor={toggleShowAddEditVendor}
                vendorID={vendorId}
                fetchVendors={() => fetchVendors(limit, page)}
                org={org}
              />
            </ResponsiveDrawerForm>
          </>
        }
        loading={vendorLoading}
      >
        {/* Vendor Table and Pagination */}
        <div className="custom-table">
          <div className="table-body-class">
            <BootstrapTable
              keyField="vendor_id"
              remote
              data={vendors}
              columns={columns}
              onTableChange={handleTableChange}
              // expandRow={expandRow}
              rowClasses={TRClasses}
              noDataIndication="No Data Found"
              classes={TableClasses}
              wrapperClasses={TableWrapClasses}
            />
          </div>

          <Pagination
            total={total}
            limit={parseInt(limit)}
            currentPage={page}
            updateLimit={handleUpdateLimit}
            updatePage={handleChangePage}
            from={from}
            to={to}
          />
        </div>
      </MainBody>

      <Confirmation
        title={`Delete ${getModuleNamingBySlug(
          organizationData?.organizationData,
          "vendors",
          "Vendor"
        )}`}
        open={showVendorDelete}
        setOpen={setShowVendorDelete}
        toggleShow={() => {
          toggleShowDelete();
        }}
        className="sm:max-w-1/2"
        message={`Are you sure you want to delete ${getModuleNamingBySlug(
          organizationData?.organizationData,
          "vendors",
          "Vendor"
        )?.toLowerCase()}?`}
        okAction={async () => {
          handleVendorDelete();
          toggleShowDelete();
          // fetchVendors(limit, page);
        }}
        cancelAction={() => {
          setVendorId(null);
          setShowVendorDelete(!showVendorDelete);
        }}
      />
    </>
  );
};

export default Vendors;
// * End: Vendor Listing Page
